import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { ScrollText, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';

const ACTIONS = ['', 'INIT', 'data_exchange', 'ping', 'BACK', 'completion'];
const PAGE_SIZE = 50;

function ActionBadge({ action }) {
  const palette = {
    INIT: 'bg-sky-50 text-sky-700 border-sky-200',
    data_exchange: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    ping: 'bg-slate-50 text-slate-600 border-slate-200',
    BACK: 'bg-amber-50 text-amber-700 border-amber-200',
    completion: 'bg-purple-50 text-purple-700 border-purple-200',
  };
  const cls = palette[action] || 'bg-slate-50 text-slate-600 border-slate-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${cls}`}>
      {action || 'unknown'}
    </span>
  );
}

export function WhatsAppLogsPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [actionFilter, setActionFilter] = useState('');
  const [flowToken, setFlowToken] = useState('');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const load = async (nextSkip = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        skip: String(nextSkip),
      });
      if (actionFilter) params.set('action', actionFilter);
      if (flowToken.trim()) params.set('flow_token', flowToken.trim());

      const [logsRes, sumRes] = await Promise.all([
        api.get(`/api/whatsapp-logs?${params.toString()}`),
        api.get('/api/whatsapp-logs/recent-summary'),
      ]);
      setLogs(logsRes.logs || []);
      setTotal(logsRes.total || 0);
      setSkip(logsRes.skip ?? nextSkip);
      setSummary(sumRes);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionFilter]);

  const next = () => total > skip + PAGE_SIZE && load(skip + PAGE_SIZE);
  const prev = () => skip > 0 && load(Math.max(0, skip - PAGE_SIZE));

  return (
    <div className="space-y-6" data-testid="whatsapp-logs-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">WhatsApp Logs</h2>
          <p className="text-sm text-slate-500 mt-1">
            Raw events from Meta Flows: INIT, data exchange, completion. Use to debug stuck flows.
          </p>
        </div>
        <Button variant="outline" onClick={() => load(skip)} className="gap-2" data-testid="logs-refresh-btn">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* 24h rollup */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="logs-summary">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Last 24 hours</h3>
        {summary?.by_action?.length ? (
          <div className="flex flex-wrap gap-3">
            {summary.by_action.map((row) => (
              <div key={row.action} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 border border-gray-100">
                <ActionBadge action={row.action} />
                <span className="text-sm font-mono font-semibold text-slate-700">{row.count}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-slate-400">No flow events in the last 24 hours.</p>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-wrap items-end gap-3" data-testid="logs-filters">
        <div className="flex-1 min-w-[180px]">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Action</p>
          <Select value={actionFilter} onValueChange={(v) => setActionFilter(v === '__all__' ? '' : v)}>
            <SelectTrigger data-testid="logs-action-select"><SelectValue placeholder="All actions" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All actions</SelectItem>
              {ACTIONS.filter(Boolean).map((a) => (
                <SelectItem key={a} value={a}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Flow token</p>
          <Input
            data-testid="logs-flowtoken-input"
            placeholder="Filter by flow_token…"
            value={flowToken}
            onChange={(e) => setFlowToken(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load(0)}
          />
        </div>
        <Button onClick={() => load(0)} className="bg-[#00D1B2] hover:bg-[#00B89C] text-white" data-testid="logs-apply-btn">
          Apply
        </Button>
      </div>

      {/* Logs table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid="logs-table">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">
            Events <span className="text-slate-400 font-normal">· {total.toLocaleString()} total</span>
          </h3>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={prev} disabled={skip === 0} data-testid="logs-prev-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-xs text-slate-500 font-mono">
              {Math.min(skip + 1, total)}–{Math.min(skip + PAGE_SIZE, total)}
            </span>
            <Button variant="ghost" size="icon" onClick={next} disabled={skip + PAGE_SIZE >= total} data-testid="logs-next-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-sm text-slate-400">Loading…</div>
        ) : logs.length === 0 ? (
          <div className="p-16 text-center">
            <ScrollText className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p className="text-sm font-medium text-slate-700">No log entries</p>
            <p className="text-xs text-slate-400 mt-1">Try clearing filters, or trigger a flow to populate this view.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {logs.map((row, idx) => {
              const id = row.id || `${row.flow_token}_${idx}`;
              const expanded = expandedId === id;
              return (
                <div key={id} data-testid={`log-row-${idx}`} className="px-5 py-3 hover:bg-slate-50/50">
                  <button
                    type="button"
                    className="w-full flex items-center gap-4 text-left"
                    onClick={() => setExpandedId(expanded ? null : id)}
                  >
                    <ActionBadge action={row.action} />
                    <span className="text-[11px] font-mono text-slate-500 truncate flex-1">
                      {row.flow_token || '—'}
                    </span>
                    {row.error && (
                      <span className="text-[10px] text-rose-600 font-semibold">ERROR</span>
                    )}
                    <span className="text-[11px] text-slate-400 font-mono shrink-0">
                      {row.timestamp ? new Date(row.timestamp).toLocaleString() : ''}
                    </span>
                  </button>
                  {expanded && (
                    <pre className="mt-3 bg-slate-900 text-emerald-200 text-[11px] p-3 rounded-md overflow-auto max-h-72 leading-relaxed">
{JSON.stringify(row, null, 2)}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
